import React, { useState, useEffect } from 'react';
import { ArrowLeft, Download, Database, Globe, Server, Shield, AlertTriangle, FileText } from 'lucide-react';
import { resultsAPI } from '../../services/api';
import ShodanResultModal from '../Results/ShodanResultModal';
import TechnologyStackResults from '../Results/TechnologyStackResults';
import PortScanResults from '../Results/PortScanResults';
import SecurityHeaderResults from '../Results/SecurityHeaderResults';
import { formatDate } from '../../utils/dateFormat';
import { getModuleDisplayName } from '../../utils/moduleNames';
import PageHeader from '../common/PageHeader';

const RESULT_TYPE_NAMES = {
  dns_record: 'DNS Records',
  subdomain: 'Subdomains',
  open_port: 'Open Ports',
  network_service: 'Network Services',
  web_technology: 'Technology Stack',
  http_header: 'HTTP Headers',
  security_header: 'Security Headers',
  ssl_info: 'TLS/SSL Information',
  shodan_data: 'Shodan Intelligence',
  web_page: 'Web Pages',
  directory: 'Directories',
  vulnerability: 'Vulnerabilities',
  certificate: 'Certificates',
  whois: 'WHOIS Data',
  email: 'Email Addresses',
};

const ScanResults = ({ scan, onBack }) => {
  const isTrial = false;
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedResult, setSelectedResult] = useState(null);
  const [showShodanModal, setShowShodanModal] = useState(false);

  useEffect(() => {
    fetchResults();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scan.id]);

  const fetchResults = async () => {
    try {
      const response = await resultsAPI.list(scan.id);
      setResults(response.data);
    } catch (error) {
      console.error('Failed to fetch results:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportResults = () => {
    const dataStr = JSON.stringify(results, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `scan-${scan.id}-results-${new Date().toISOString()}.json`;
    link.click();
  };

  const getTypeIcon = (type) => {
    const icons = {
      subdomain: Globe,
      ip: Server,
      port: Shield,
      vulnerability: AlertTriangle,
      technology: Server
    };
    return icons[type] || Database;
  };

  const handleResultClick = (result) => {
    if (result.result_type === 'shodan_data') {
      setSelectedResult(result);
      setShowShodanModal(true);
    }
  };

  const getTypeColor = (type) => {
    const colors = {
      subdomain: 'text-shinobi-purple',
      ip: 'text-shinobi-orange',
      port: 'text-blue-600',
      shodan_data: 'text-purple-600',
      vulnerability: 'text-danger-red',
      technology: 'text-yellow-600'
    };
    return colors[type] || 'text-slate-500';
  };

  // Group results by type
  const groupedResults = results.reduce((acc, result) => {
    const key = result.result_type;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(result);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-shinobi-purple">Loading...</div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Scan Results"
        subtitle={`${getModuleDisplayName(scan.module_name)} • ${formatDate(scan.created_at)}`}
        icon={FileText}
        showBackButton={true}
        onBack={onBack}
        actions={
          results.length > 0 && (
            <button
              onClick={exportResults}
              className="btn-cyber px-4 py-2 rounded flex items-center space-x-2"
            >
              <Download className="w-4 h-4" />
              <span>Export JSON</span>
            </button>
          )
        }
      />
      <div className="p-8">

      {/* Stats Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="cyber-card rounded-lg p-4">
          <div className="text-2xl font-bold text-shinobi-purple">{results.length}</div>
          <div className="text-sm text-slate-500">Total Results</div>
        </div>
        <div className="cyber-card rounded-lg p-4">
          <div className="text-2xl font-bold text-shinobi-orange">{Object.keys(groupedResults).length}</div>
          <div className="text-sm text-slate-500">Result Types</div>
        </div>
        <div className="cyber-card rounded-lg p-4">
          <div className="text-2xl font-bold text-yellow-600">{results.filter(r => r.is_new).length}</div>
          <div className="text-sm text-slate-500">New Findings</div>
        </div>
        <div className="cyber-card rounded-lg p-4">
          <div className="text-2xl font-bold text-slate-500">{scan.duration ? `${Math.floor(scan.duration / 60)}m` : 'N/A'}</div>
          <div className="text-sm text-slate-500">Scan Duration</div>
        </div>
      </div>

      {/* Error Message */}
      {scan.error_message && (
        <div className="cyber-card rounded-lg p-6 mb-8 border-2 border-danger-red/30 bg-red-900/10">
          <div className="flex items-start space-x-3">
            <AlertTriangle className="w-6 h-6 text-danger-red flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-lg font-semibold text-danger-red mb-2">Scan Error</h3>
              <p className="text-slate-700">{scan.error_message}</p>
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {results.length === 0 ? (
        <div className="cyber-card rounded-lg p-12 text-center">
          <Database className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-slate-700 mb-2">No Results</h3>
          <p className="text-gray-500">
            {scan.error_message ? 'The scan encountered an error and did not complete successfully.' : 'This scan did not find any results'}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedResults).map(([type, typeResults]) => {
            const TypeIcon = getTypeIcon(type);
            const typeColor = getTypeColor(type);

            // Special rendering for technology stack
            if (type === 'web_technology') {
              return (
                <div key={type} className="cyber-card rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4 pb-3 border-b border-slate-200">
                    <TypeIcon className={`w-6 h-6 ${typeColor}`} />
                    <h2 className="text-xl font-bold text-slate-800 uppercase">Technology Stack</h2>
                    <span className="px-3 py-1 rounded text-xs font-semibold bg-slate-100 text-slate-500">
                      {typeResults.length} technologies
                    </span>
                  </div>
                  <TechnologyStackResults results={typeResults} />
                  </div>
              );
            }

            // Special rendering for port scan / network service results
            if (type === 'open_port' || type === 'network_service') {
              const sectionTitle = type === 'network_service' ? 'Network Services' : 'Open Ports';
              return (
                <div key={type} className="cyber-card rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4 pb-3 border-b border-slate-200">
                    <TypeIcon className={`w-6 h-6 ${typeColor}`} />
                    <h2 className="text-xl font-bold text-slate-800 uppercase">{sectionTitle}</h2>
                    <span className="px-3 py-1 rounded text-xs font-semibold bg-slate-100 text-slate-500">
                      {typeResults.length} {type === 'network_service' ? 'services' : 'ports'}
                    </span>
                  </div>
                  <PortScanResults results={typeResults} />
                  </div>
              );
            }

            // Special rendering for security headers
            if (type === 'security_header' || type === 'http_header') {
              return (
                <div key={type} className="cyber-card rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4 pb-3 border-b border-slate-200">
                    <TypeIcon className={`w-6 h-6 ${typeColor}`} />
                    <h2 className="text-xl font-bold text-slate-800 uppercase">Security Headers</h2>
                    <span className="px-3 py-1 rounded text-xs font-semibold bg-slate-100 text-slate-500">
                      {typeResults.length} headers
                    </span>
                  </div>
                  <SecurityHeaderResults results={typeResults} />
                  </div>
              );
            }

            {
              const BLUR_TYPES = ['subdomain', 'dns_record'];
              const shouldBlur = isTrial && BLUR_TYPES.includes(type) && typeResults.length > 1;
              const visibleResults = shouldBlur ? typeResults.slice(0, 1) : typeResults;
              const hiddenResults = shouldBlur ? typeResults.slice(1) : [];
              const typeLabel = type === 'subdomain' ? 'subdomains' : type === 'dns_record' ? 'DNS records' : 'results';

              const renderResultCard = (result) => (
                <div
                  key={result.id}
                  onClick={() => handleResultClick(result)}
                  className={`bg-slate-50 rounded p-3 hover:bg-slate-100 transition-colors ${
                    result.result_type === 'shodan_data' ? 'cursor-pointer hover:border-purple-500 border border-transparent' : ''
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <p className="text-sm text-slate-800 font-mono break-all flex-1">
                      {result.value}
                    </p>
                    {result.is_new && (
                      <span className="ml-2 px-2 py-0.5 rounded text-xs font-semibold bg-shinobi-purple/10 text-shinobi-purple flex-shrink-0">
                        NEW
                      </span>
                    )}
                  </div>

                  {result.metadata && Object.keys(result.metadata).length > 0 && (
                    <details className="mt-2">
                      <summary className="text-xs text-gray-500 cursor-pointer hover:text-slate-500">
                        View metadata
                      </summary>
                      <pre className="text-xs text-slate-500 mt-2 overflow-x-auto bg-slate-100 p-2 rounded">
                        {JSON.stringify(result.metadata, null, 2)}
                      </pre>
                    </details>
                  )}
                </div>
              );

              return (
                <div key={type} className="cyber-card rounded-lg p-6">
                  <div className="flex items-center space-x-3 mb-4 pb-3 border-b border-slate-200">
                    <TypeIcon className={`w-6 h-6 ${typeColor}`} />
                    <h2 className="text-xl font-bold text-slate-800 uppercase">{RESULT_TYPE_NAMES[type] || type.replace(/_/g, ' ')}</h2>
                    <span className="px-3 py-1 rounded text-xs font-semibold bg-slate-100 text-slate-500">
                      {typeResults.length} found
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {visibleResults.map(renderResultCard)}
                  </div>

                  {shouldBlur && (
                    <div className="mt-3">
                      
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                          {hiddenResults.map(renderResultCard)}
                        </div>
                      
                    </div>
                  )}
                  </div>
              );
            }
          })}
        </div>
      )}

      {/* Shodan Result Modal */}
      <ShodanResultModal
        isOpen={showShodanModal}
        onClose={() => setShowShodanModal(false)}
        result={selectedResult}
      />
      </div>
    </div>
  );
};

export default ScanResults;
